import { z } from 'zod';
import { CustomFieldMappingContextValueSchema } from '../models.connection_settings';
import { TimelyAllowedSideEffectsSchema } from '../timely/models.timely.side_effects';

/**
 * Resource Metadata Base Model.
 */

// Providers store lightweight label references in metadata rather than creating a new association
// table.  Timely relies on both identifiers to reconcile tags across multiple diffs.
const ResourceMetadataTagSchema = z.object({
  external_id: z.string().describe('External tag ID from provider'),
  uri: z.string().describe('TIC synthetic URI for the tag'),
  title: z.string().nullable().optional().describe('Tag display name'),
});

/**
 * Structured project reference for task resources.
 * Enables multi-identifier lookup on Timely side for reliable project resolution.
 */
const ResourceMetadataProjectSchema = z.object({
  /** Provider's external ID for the project (e.g., Asana project GID, Linear project ID) */
  external_id: z.string().describe('External project ID from provider'),
  /** TIC synthetic URI for the project */
  uri: z.string().describe('TIC synthetic URI for the project'),
});

/**
 * Describes how a task observed a provider custom field that maps to a stored TIC client.
 * This is provenance only; the client itself is imported as a normal resource.
 */
const ResourceMetadataClientSourceSchema = z.object({
  provider: z.string().describe('Provider that produced the custom-field client reference'),
  mapping_id: z
    .string()
    .describe('connection.settings.custom_field_mappings[].id used for this match'),
  field_id: z.string().describe('Provider custom field identifier used for this match'),
  option_id: z.string().describe('Provider option identifier used for stored client identity'),
  context: z
    .record(z.string(), CustomFieldMappingContextValueSchema)
    .default({})
    .describe('Provider-specific mapping context snapshot used to rediscover the field'),
  option_label: z.string().nullable().optional().describe('Provider option label snapshot'),
});

/**
 * Structured client reference for task resources.
 * The hierarchy stays task -> project -> client; this field records custom-field context only.
 */
const ResourceMetadataClientSchema = z.object({
  uri: z.string().describe('Stored TIC client resource URI referenced by this task'),
  external_id: z
    .string()
    .optional()
    .describe('Stored TIC client external_id, included for diagnostics/provenance'),
  source: ResourceMetadataClientSourceSchema.describe(
    'How this task was linked to the stored client'
  ),
});

export const ResourceMetadataWriteSchema = z.object({
  /**
   * What is the resource called externally? Not to be confused with `resource.type`.
   * @example `repo` for Github repositories (that would also have `resource.type` as 'project')
   */
  external_type: z.string().describe('Provider-specific resource type name'),
  /**
   * A code/reference/identifier from the external system, non-unique.
   * e.g `Frontend` repo in Github, JIRA-123 for a JIRA issue, etc.
   */
  external_code: z.coerce
    .string()
    .nullable()
    .describe('Human-readable external ID (e.g., PROJ-123)'),
  /** Any description available for this resource, e.g PR descriptin, issue body, etc. */
  description: z.string().nullable().describe('Resource description or body text'),
  /** URL to an icon representing this resource. Leave null will use the tool's icon whenever needed.  */
  icon_url: z.string().url().nullable().describe('Resource-specific icon URL'),
  /** Does this resource support webhooks? */
  supports_webhooks: z
    .boolean()
    .default(false)
    .describe('Whether resource supports webhook subscriptions'),
  /** Optional capability hint for providers that can detect nested subtask/subitem support. */
  supports_subitems: z
    .boolean()
    .nullable()
    .optional()
    .describe('Whether resource supports nested subtasks/subitems'),
  /** Color of the resource, e.g, project color, task color, etc. */
  color: z.coerce.string().nullable().default(null).describe('Resource color (hex or name)'),
  /**
   * Optional set of tag resources associated with this resource.
   * Each entry references the tag's external identifier and synthetic URI.
   */
  tags: z.array(ResourceMetadataTagSchema).nullable().optional().describe('Associated tags/labels'),
  /**
   * Every member team URI of a Linear project. A Linear project can belong to several teams but
   * the shared resource row stores one `parent_id`; the Timely diff reads this list so the
   * project reaches a connection that syncs any of its teams (TT-6077).
   */
  team_uris: z
    .array(z.string())
    .nullable()
    .optional()
    .describe('Complete member team URIs for Linear project subscription eligibility'),
  /**
   * Structured project reference for task resources.
   *
   * **Purpose:** Enables correct project resolution for nested task hierarchies
   * (e.g., Asana subtasks, Linear sub-issues) where the direct parent is another task,
   * not the project. Providers populate this during import.
   *
   * **Usage:**
   * - Set on all task resources during provider import
   * - Contains `{ external_id, uri }` for multi-identifier lookup
   * - Takes precedence over parent_uri chain traversal in Timely export
   * - Timely uses `project_by_identifiers(tic_id:, tic_uri:, external_id:)` for resolution
   *
   * @example project: { external_id: '12345', uri: 'tic://synthetic/asana/projects/12345' }
   */
  project: ResourceMetadataProjectSchema.nullable()
    .optional()
    .describe('Primary project reference for task resolution'),
  /**
   * Additional project references for tasks in multiple projects.
   * The primary project is stored in `project`; remaining projects here.
   * Sorted by GID (oldest first after primary).
   */
  additional_projects: z
    .array(ResourceMetadataProjectSchema)
    .nullable()
    .optional()
    .describe('Secondary project references for multi-project tasks'),
  /**
   * Estimated time to complete the task, in minutes.
   * Providers convert from their native format (e.g., ClickUp uses milliseconds).
   * Used by Timely for forecast estimated_minutes field.
   */
  estimated_minutes: z.number().nullable().optional().describe('Estimated time in minutes'),
  /**
   * Client reference observed through a custom field mapping.
   * The referenced client is imported as a stored TIC resource; this does not alter hierarchy.
   */
  client: ResourceMetadataClientSchema.nullable()
    .optional()
    .describe('Stored client reference from custom field mapping'),
  allowed_side_effects: TimelyAllowedSideEffectsSchema.optional().describe(
    'Timely side effects this resource is explicitly allowed to trigger'
  ),
});
export type ResourceMetadataWrite = z.infer<typeof ResourceMetadataWriteSchema>;

export const ResourceMetadataReadSchema = ResourceMetadataWriteSchema.extend({});
export type ResourceMetadataRead = z.infer<typeof ResourceMetadataReadSchema>;
